import React, { useState, useEffect } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { 
  MdAttachMoney, 
  MdAdd, 
  MdTrendingUp, 
  MdTrendingDown, 
  MdAccountBalance,
  MdDescription,
  MdPayment,
  MdMonetizationOn,
  MdReceipt,
  MdEdit,
  MdCheck,
  MdSave,
  MdNotes,
  MdTimeline
} from 'react-icons/md';
import { api } from '../services/api';
import Toast from '../components/Toast';
import { useInstallments } from '../hooks/useInstallments';
import { getLocalDateInputValue } from '../utils/dateUtils';

const formatCurrencyInput = (value) => {
  const digits = (value || '').replace(/\D/g, '');
  const number = parseFloat(digits) / 100 || 0;
  return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseAmountToNumber = (formattedAmount = '') => {
  return parseFloat(formattedAmount.replace(/\./g, '').replace(',', '.')) || 0;
};

const formatCurrency = (value = 0) => {
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(absValue);
  return isNegative ? formatted.replace('R$', 'R$ -') : formatted;
};

const formatDate = (date) => new Date(date).toLocaleDateString('pt-BR');

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const avatarTone = (name = '') => {
  const palettes = [
    { bg: '#FBE0E5', fg: '#9F4A60' },
    { bg: '#EFE3F0', fg: '#7A4A86' },
    { bg: '#FBEEDF', fg: '#A66730' },
    { bg: '#E5DCEC', fg: '#634A82' },
    { bg: '#F4DBDE', fg: '#A8556A' },
    { bg: '#E6F1EB', fg: '#3D8068' },
    { bg: '#E8E3DA', fg: '#65564A' },
  ];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return palettes[h % palettes.length];
};

const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function Financeiro() {
  const { currentUser, patients = [] } = useOutletContext();
  const location = useLocation();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [viewingInstallments, setViewingInstallments] = useState([]);
  const [keepFormOpen, setKeepFormOpen] = useState(false);
  const [paidInstallmentsInfo, setPaidInstallmentsInfo] = useState([]); // parcelas já pagas do grupo em edição
  
  // Filtros
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState(location.state?.filterStatus || '');
  const [filterCategory, setFilterCategory] = useState('');
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' });
  const [filterMode, setFilterMode] = useState(location.state?.filterStatus ? 'period' : 'month'); // 'month', 'year', 'period'
  const [startDate, setStartDate] = useState(location.state?.filterStatus ? '2000-01-01' : '');
  const [endDate, setEndDate] = useState(location.state?.filterStatus ? new Date().toISOString().slice(0, 10) : '');
  
  // Formulário
  const [formData, setFormData] = useState({
    type: 'receita',
    category: '',
    description: '',
    amount: '',
    date: getLocalDateInputValue(),
    paymentStatus: 'pendente',
    paymentMethod: 'dinheiro',
    patientId: '',
    supplier: '',
    notes: '',
    installments: 1,
    hasInstallments: false,
    installmentPaymentMethod: 'cartao_credito'
  });

  const {
    installmentSchedule,
    handleInstallmentFieldChange,
    recalculateInstallments,
    unlockSchedule,
    overrideSchedule
  } = useInstallments(formData, parseAmountToNumber, formatDateForInput);

  // Categorias predefinidas
  const categories = {
    receita: ['Consulta', 'Procedimento', 'Exame', 'Produto', 'Outro'],
    despesa: ['Salário', 'Aluguel', 'Material', 'Equipamento', 'Serviço', 'Outro']
  };

  useEffect(() => {
    fetchTransactions();
    fetchSummary();
  }, [filterType, filterStatus, filterCategory, selectedMonth, selectedYear, filterMode, startDate, endDate]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showPatientDropdown) {
        // Verifica se clicou no input ou dentro do dropdown
        const isInputClick = e.target.closest('input[placeholder="Digite para buscar..."]');
        const isDropdownClick = e.target.closest('[data-patient-dropdown]');
        
        if (!isInputClick && !isDropdownClick) {
          setShowPatientDropdown(false);
        }
      }
      
      if (editingStatusId) {
        const isStatusClick = e.target.closest('[data-status-dropdown]');
        if (!isStatusClick) {
          setEditingStatusId(null);
        }
      }
    };

    // Usar timeout para permitir que onClick dos itens seja processado primeiro
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPatientDropdown, editingStatusId]);

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('type', filterType);
      // 'atrasado' é calculado no frontend (pendente + data passada), não existe no banco
      if (filterStatus && filterStatus !== 'atrasado') params.append('paymentStatus', filterStatus);
      if (filterCategory) params.append('category', filterCategory);
      
      if (filterMode === 'month') {
        params.append('month', selectedMonth);
        params.append('year', selectedYear);
      } else if (filterMode === 'year') {
        params.append('year', selectedYear);
      } else if (filterMode === 'period' && startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      
      const response = await api.get(`/financial?${params}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Erro ao buscar transações:', error);
      showToast('Erro ao carregar transações', 'error');
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      
      // Adiciona filtros de tipo, status e categoria
      if (filterType) params.append('type', filterType);
      if (filterStatus && filterStatus !== 'atrasado') params.append('paymentStatus', filterStatus);
      if (filterCategory) params.append('category', filterCategory);
      
      if (filterMode === 'month') {
        params.append('month', selectedMonth);
        params.append('year', selectedYear);
      } else if (filterMode === 'year') {
        params.append('year', selectedYear);
      } else if (filterMode === 'period' && startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      
      const response = await api.get(`/financial/summary?${params}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Erro ao buscar resumo:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.hasInstallments) {
        const totalAmountValue = parseAmountToNumber(formData.amount);
        const totalInstallmentsValue = installmentSchedule.reduce((sum, inst) => {
          if (!inst.amount) return sum;
          return sum + parseAmountToNumber(inst.amount);
        }, 0);
        const difference = totalAmountValue - totalInstallmentsValue;
        if (Math.abs(difference) > 0.01) {
          showToast(`A soma das parcelas deve ser igual ao valor total. Diferença: ${formatCurrency(difference)}`, 'error');
          return;
        }
      }
      
      const data = {
        ...formData,
        amount: parseAmountToNumber(formData.amount),
      };
      
      // Converter a data para o formato correto (mantém o dia local)
      if (data.date) {
        const [year, month, day] = data.date.split('-').map(Number);
        data.date = new Date(year, month - 1, day).toISOString();
      }
      
      // Remove patientId se estiver vazio
      if (!data.patientId) {
        delete data.patientId;
      }

      // Se não tem parcelamento, limpar campos relacionados
      if (!data.hasInstallments) {
        data.installments = 1;
        delete data.installmentPaymentMethod;
        delete data.customInstallments;
      } else if (installmentSchedule.length > 0) {
        data.customInstallments = installmentSchedule.map((inst, index) => ({
          amount: parseAmountToNumber(inst.amount),
          paymentMethod: inst.paymentMethod,
          date: inst.date ? (() => { const [y,m,d] = inst.date.split('-').map(Number); return new Date(y, m-1, d).toISOString(); })() : undefined,
          installmentNumber: inst.installmentNumber || index + 1,
          paymentStatus: inst.paymentStatus
        }));
      } else {
        delete data.customInstallments;
      }

      if (editingId) {
        await api.put(`/financial/${editingId}`, data);
        showToast('Transação atualizada com sucesso!', 'success');
      } else {
        await api.post('/financial', data);
        showToast('Transação criada com sucesso!', 'success');
      }
      
      if (keepFormOpen && !editingId) {
        // Limpa apenas os campos principais, mantém o formulário aberto
        setFormData({
          ...formData,
          description: '',
          amount: '',
          patientId: '',
          notes: ''
        });
        setPatientSearch('');
        overrideSchedule([]);
        unlockSchedule();
      } else {
        resetForm();
      }
      fetchTransactions();
      fetchSummary();
    } catch (error) {
      console.error('Erro ao salvar transação:', error);
      showToast('Erro ao salvar transação', 'error');
    }
  };

  const handleViewTransaction = async (transaction) => {
    setViewingTransaction(transaction);
    
    // Se for uma transação parcelada, buscar todas as parcelas do grupo
    if (transaction.installmentGroup) {
      try {
        const response = await api.get(`/financial/group/${transaction.installmentGroup}`);
        setViewingInstallments(response.data);
      } catch (error) {
        console.error('Erro ao buscar parcelas:', error);
        setViewingInstallments([]);
      }
    } else {
      setViewingInstallments([]);
    }
  };

  // Remove sufixos de parcela/entrada acumulados na descrição
  const stripInstallmentSuffix = (desc) =>
    (desc || '').replace(/(\s*-\s*(Parcela\s+\d+\/\d+|Entrada(\s+\d+)?))+$/g, '').trim();

  const handleEdit = async (transaction) => {
    // Se for uma transação parcelada, buscar todas as parcelas do grupo primeiro
    let allInstallments = [];
    if (transaction.installmentGroup) {
      try {
        const response = await api.get(`/financial/group/${transaction.installmentGroup}`);
        allInstallments = response.data;
      } catch (error) {
        console.error('Erro ao buscar parcelas:', error);
      }
    }
    
    // Corrige timezone da data
    const transactionDate = new Date(transaction.date);
    const year = transactionDate.getFullYear();
    const month = String(transactionDate.getMonth() + 1).padStart(2, '0');
    const day = String(transactionDate.getDate()).padStart(2, '0');
    const localDateString = `${year}-${month}-${day}`;
    
    // Se for parcelado, reconstruir os dados originais
    let formDataToSet;
    if (allInstallments.length > 0) {
      const sortedInstallments = allInstallments
        .filter(t => t.installmentNumber !== null && t.installmentNumber >= 0)
        .sort((a, b) => a.installmentNumber - b.installmentNumber);

      // Todas as outras parcelas do grupo (pagas + pendentes) que NÃO são a que está sendo editada
      const otherInst = sortedInstallments.filter(t => t._id !== transaction._id);
      setPaidInstallmentsInfo(otherInst); // banner mostra tudo que será preservado

      const originalDescription = stripInstallmentSuffix(transaction.description);

      // Formulário mostra APENAS a parcela clicada
      formDataToSet = {
        type: transaction.type,
        category: transaction.category,
        description: originalDescription,
        amount: transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        date: localDateString,
        paymentStatus: transaction.paymentStatus,
        paymentMethod: transaction.paymentMethod,
        patientId: transaction.patientId || '',
        supplier: transaction.supplier || '',
        notes: (() => {
          const today = new Date().toLocaleDateString('pt-BR');
          const reparcelNote = `[${today}] Reparcelamento da Parcela ${transaction.installmentNumber}/${transaction.totalInstallments || '?'} – R$ ${transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          return transaction.notes ? `${transaction.notes}\n${reparcelNote}` : reparcelNote;
        })(),
        installments: 1,
        hasInstallments: true,
        installmentPaymentMethod: transaction.paymentMethod || 'cartao_credito'
      };

      // Schedule começa com só esta parcela — usuário pode adicionar linhas para subdividir
      overrideSchedule([{
        installmentNumber: transaction.installmentNumber || 1,
        amount: transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        date: localDateString,
        paymentMethod: transaction.paymentMethod,
        paymentStatus: transaction.paymentStatus
      }]);
    } else {
      // Transação simples
      setPaidInstallmentsInfo([]);
      formDataToSet = {
        type: transaction.type,
        category: transaction.category,
        description: stripInstallmentSuffix(transaction.description) || transaction.description,
        amount: transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        date: localDateString,
        paymentStatus: transaction.paymentStatus,
        paymentMethod: transaction.paymentMethod,
        patientId: transaction.patientId || '',
        supplier: transaction.supplier || '',
        notes: transaction.notes || '',
        installments: 1,
        hasInstallments: false,
        installmentPaymentMethod: 'cartao_credito'
      };
      overrideSchedule([]);
      unlockSchedule();
    }
    
    setFormData(formDataToSet);
    
    // Preencher o nome do paciente se existir
    if (transaction.patientId) {
      const patient = patients.find(p => (p._id || p.id) === transaction.patientId);
      if (patient) {
        setPatientSearch(patient.name);
      }
    } else {
      setPatientSearch('');
    }
    
    setEditingId(transaction._id);
    setShowForm(true);
  };

  const handleDelete = async (id, installmentGroup = null) => {
    if (!installmentGroup) {
      // Transação normal
      if (!window.confirm('Deseja realmente excluir esta transação?')) return;
      
      try {
        await api.delete(`/financial/${id}`);
        showToast('Transação excluída com sucesso!', 'success');
        fetchTransactions();
        fetchSummary();
      } catch (error) {
        console.error('Erro ao excluir transação:', error);
        showToast('Erro ao excluir transação', 'error');
      }
    } else {
      // Parcela - perguntar se quer excluir todas
      const deleteAll = window.confirm(
        'Esta é uma parcela de um grupo. Deseja excluir TODAS as parcelas deste grupo?\n\n' +
        'Sim = Excluir todas as parcelas\n' +
        'Não = Excluir apenas esta parcela'
      );
      
      try {
        if (deleteAll) {
          await api.delete(`/financial/${id}?deleteGroup=true`);
          showToast('Todas as parcelas foram excluídas!', 'success');
        } else {
          await api.delete(`/financial/${id}`);
          showToast('Parcela excluída com sucesso!', 'success');
        }
        fetchTransactions();
        fetchSummary();
      } catch (error) {
        console.error('Erro ao excluir:', error);
        showToast('Erro ao excluir', 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'receita',
      category: '',
      description: '',
      amount: '',
      date: getLocalDateInputValue(),
      paymentStatus: 'pendente',
      paymentMethod: 'dinheiro',
      patientId: '',
      supplier: '',
      notes: '',
      installments: 1,
      hasInstallments: false,
      installmentPaymentMethod: 'cartao_credito'
    });
    setPatientSearch('');
    setShowPatientDropdown(false);
    setEditingId(null);
    setPaidInstallmentsInfo([]);
    overrideSchedule([]);
    unlockSchedule();
    setShowForm(false);
  };

  const formatCurrencyInput = (value) => {
    // Remove tudo que não é dígito
    const digits = value.replace(/\D/g, '');
    // Converte para número e divide por 100 para ter os centavos
    const number = parseFloat(digits) / 100 || 0;
    // Formata para moeda brasileira
    return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleAmountChange = (e) => {
    const formatted = formatCurrencyInput(e.target.value);
    setFormData({ ...formData, amount: formatted });
    unlockSchedule();
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const getStatusColor = (status) => {
    const colors = {
      pago: '#22c55e',
      pendente: '#f59e0b',
      atrasado: '#ef4444',
      vence_hoje: '#8b5cf6'
    };
    return colors[status] || '#6b7280';
  };


  const handleStatusChange = async (transactionId, newStatus) => {
    try {
      await api.put(`/financial/${transactionId}`, { paymentStatus: newStatus });
      showToast('Status atualizado!', 'success');
      fetchTransactions();
      fetchSummary();
      setEditingStatusId(null);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showToast('Erro ao atualizar status', 'error');
    }
  };

  const getEffectiveStatus = (transaction) => {
    if (transaction.paymentStatus === 'pendente') {
      const transactionDate = new Date(transaction.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      transactionDate.setHours(0, 0, 0, 0);
      
      if (transactionDate < today) {
        return 'atrasado';
      } else if (transactionDate.getTime() === today.getTime()) {
        return 'vence_hoje';
      }
    }
    return transaction.paymentStatus;
  };

  // Filtrar transações por busca e status efetivo
  const filteredTransactions = transactions.filter((transaction) => {
    // 'atrasado' não está no banco — filtrar client-side pelo status calculado
    if (filterStatus === 'atrasado' && getEffectiveStatus(transaction) !== 'atrasado') return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const searchTerms = query.split(' ').filter(term => term.length > 0);
    
    // Buscar nome e CPF do paciente se houver
    let patientName = '';
    let patientCpf = '';
    if (transaction.patientId) {
      const patient = patients.find(p => (p._id || p.id) === transaction.patientId);
      if (patient) {
        patientName = patient.name;
        patientCpf = patient.cpf || '';
      }
    }
    
    // Obter status efetivo (incluindo "vence hoje" e "atrasado")
    const effectiveStatus = getEffectiveStatus(transaction);
    const statusText = effectiveStatus === 'vence_hoje' ? 'vence hoje' : effectiveStatus;
    
    const searchableText = [
      transaction.description,
      transaction.category,
      transaction.type,
      transaction.paymentStatus,
      statusText,
      transaction.paymentMethod,
      formatCurrency(transaction.amount),
      formatDate(transaction.date),
      transaction.notes || '',
      patientName,
      patientCpf
    ].join(' ').toLowerCase();
    
    return searchTerms.every(term => searchableText.includes(term));
  });

  // Ordenar transações
  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortConfig.key) {
      case 'date':
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
        break;
      case 'type':
        aValue = a.type.toLowerCase();
        bValue = b.type.toLowerCase();
        break;
      case 'category':
        aValue = a.category.toLowerCase();
        bValue = b.category.toLowerCase();
        break;
      case 'description':
        aValue = a.description.toLowerCase();
        bValue = b.description.toLowerCase();
        break;
      case 'amount':
        aValue = a.amount;
        bValue = b.amount;
        break;
      case 'paymentStatus':
        aValue = a.paymentStatus.toLowerCase();
        bValue = b.paymentStatus.toLowerCase();
        break;
      case 'paymentMethod':
        aValue = a.paymentMethod.toLowerCase();
        bValue = b.paymentMethod.toLowerCase();
        break;
      case 'patientId':
        aValue = (a.patientId
          ? patients.find(p => (p._id || p.id) === a.patientId)?.name || ''
          : a.supplier || '').toLowerCase();
        bValue = (b.patientId
          ? patients.find(p => (p._id || p.id) === b.patientId)?.name || ''
          : b.supplier || '').toLowerCase();
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Calcular resumo baseado nas transações filtradas (incluindo busca)
  const filteredSummary = React.useMemo(() => {
    if (!filteredTransactions.length) {
      return {
        receitasRecebidas: 0,
        despesasPagas: 0,
        receitasAtrasadas: 0,
        saldo: 0,
        saldoProjetado: 0
      };
    }

    const receitasRecebidas = filteredTransactions
      .filter(t => t.type === 'receita' && t.paymentStatus === 'pago')
      .reduce((sum, t) => sum + t.amount, 0);

    const despesasPagas = filteredTransactions
      .filter(t => t.type === 'despesa' && t.paymentStatus === 'pago')
      .reduce((sum, t) => sum + t.amount, 0);

    const receitasPendentes = filteredTransactions
      .filter(t => t.type === 'receita' && t.paymentStatus === 'pendente')
      .reduce((sum, t) => sum + t.amount, 0);

    const despesasPendentes = filteredTransactions
      .filter(t => t.type === 'despesa' && t.paymentStatus === 'pendente')
      .reduce((sum, t) => sum + t.amount, 0);

    const saldo = receitasRecebidas - despesasPagas;
    const saldoProjetado = saldo + receitasPendentes - despesasPendentes;

    const receitasAtrasadas = filteredTransactions
      .filter(t => t.type === 'receita' && getEffectiveStatus(t) === 'atrasado')
      .reduce((sum, t) => sum + t.amount, 0);

    return {
      receitasRecebidas,
      despesasPagas,
      receitasAtrasadas,
      saldo,
      saldoProjetado
    };
  }, [filteredTransactions]);

  const totalInstallmentAmount = React.useMemo(() => {
    return installmentSchedule.reduce((sum, inst) => {
      if (!inst.amount) return sum;
      return sum + parseAmountToNumber(inst.amount);
    }, 0);
  }, [installmentSchedule]);

  const totalAmountValue = parseAmountToNumber(formData.amount || '0');
  const scheduleCount = installmentSchedule.length || formData.installments;
  const differenceAmount = totalAmountValue - totalInstallmentAmount;

  return (
    <div className="patients-card" style={{ paddingTop: 44, paddingBottom: 56, color: '#1C1C1E' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#FBE0E5', color: '#9F4A60',
            padding: '4px 10px', borderRadius: 999,
            fontSize: 11, letterSpacing: '-0.005em', fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: '#C97A8F' }} />
            Gestão financeira
          </div>
          <h1 style={{ fontWeight: 700, fontSize: 38, lineHeight: 1.18, letterSpacing: '-0.035em', margin: '10px 0 0', color: '#1C1C1E' }}>
            Financeiro
          </h1>
          <div style={{ marginTop: 6, fontSize: 14, color: '#6E6E73', letterSpacing: '-0.005em' }}>
            Controle receitas, despesas e saldos da clínica
          </div>
        </div>
        <button className="mac-button" onClick={() => setShowForm(!showForm)} style={{ marginTop: 6 }}>
          + {showForm ? 'Cancelar' : 'Nova Transação'}
        </button>
      </div>

      {/* KPI Cards */}
      {(() => {
        const renderKpiIcon = (kind, color) => {
          const s = { width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
          if (kind === 'up')         return <svg {...s}><path d="M3 17l5-6 4 3 4-5 5 4M14 7h5v5"/></svg>;
          if (kind === 'down')       return <svg {...s}><path d="M3 7l5 6 4-3 4 5 5-4M14 17h5v-5"/></svg>;
          if (kind === 'bank')       return <svg {...s}><path d="M3 9l9-5 9 5M5 9v9M19 9v9M9 9v9M15 9v9M3 19h18M3 9h18"/></svg>;
          if (kind === 'projection') return <svg {...s}><path d="M3 17l6-6 4 4 8-8M21 3v6h-6M5 21h14"/></svg>;
          if (kind === 'alert')      return <svg {...s}><circle cx="12" cy="12" r="9"/><path d="M12 7v6M12 16.5v.5"/></svg>;
          return null;
        };
        const kpis = [
          { label: 'Receitas Recebidas',   value: filteredSummary.receitasRecebidas,  subtitle: `${filteredTransactions.filter(t => t.type === 'receita' && t.paymentStatus === 'pago').length} entradas`,          accent: '#3D8068', bg: ['#E6F1EB', '#D2E5DC'], icon: 'up' },
          { label: 'Receitas Atrasadas',    value: filteredSummary.receitasAtrasadas,  subtitle: `${filteredTransactions.filter(t => t.type === 'receita' && getEffectiveStatus(t) === 'atrasado').length} em atraso`, accent: '#A8556A', bg: ['#FBE0E5', '#EFBCC6'], icon: 'alert' },
          { label: 'Despesas Pagas',        value: filteredSummary.despesasPagas,      subtitle: `${filteredTransactions.filter(t => t.type === 'despesa' && t.paymentStatus === 'pago').length} saídas`,            accent: '#9F4A60', bg: ['#FBE0E5', '#F4C4CE'], icon: 'down' },
          { label: 'Saldo Realizado',       value: filteredSummary.saldo,              subtitle: 'Receitas − Despesas pagas',                                                                                         accent: '#1C1C1E', bg: ['#F5F5F7', '#E8E8ED'], icon: 'bank' },
          { label: 'Saldo Projetado',       value: filteredSummary.saldoProjetado,     subtitle: 'Inclui pendentes',                                                                                                  accent: '#7A4A86', bg: ['#EFE3F0', '#DECCDE'], icon: 'projection' },
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
            {kpis.map((k) => (
              <div key={k.label} style={{ background: `linear-gradient(160deg, ${k.bg[0]} 0%, ${k.bg[1]} 100%)`, borderRadius: 22, padding: '18px', minHeight: 130, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), 0 1px 2px rgba(28,28,30,0.04), 0 8px 24px -16px rgba(28,28,30,0.18)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(28,28,30,0.65)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.label}</div>
                  <div style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)', flexShrink: 0, marginLeft: 6 }}>
                    {renderKpiIcon(k.icon, k.accent)}
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(28,28,30,0.55)' }}>R$</span>
                    <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.04em', color: k.accent, fontVariantNumeric: 'tabular-nums' }}>
                      {Math.abs(k.value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11.5, color: 'rgba(28,28,30,0.6)', letterSpacing: '-0.005em' }}>{k.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Modal de Nova Transação */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px'
        }} onClick={resetForm}>
          <div 
            className="form-card" 
            style={{ 
              maxWidth: '800px', 
              width: '100%',
              maxHeight: '90vh',
              padding: 0,
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '32px'
            }}>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '2px solid #f3f4f6'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '24px', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {editingId ? <><MdEdit /> Editar Transação</> : <><MdAdd /> Nova Transação</>}
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                  Preencha as informações da {formData.type}
                </p>
              </div>
              <button 
                className="ghost-button" 
                onClick={resetForm}
                style={{ fontSize: '1.5rem', padding: '4px 12px' }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {/* Banner: outras parcelas do grupo (preservadas) */}
              {paidInstallmentsInfo.length > 0 && (
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  fontSize: '13px',
                  color: '#1d4ed8'
                }}>
                  <strong>Outras parcelas do grupo (serão preservadas):</strong>
                  <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px' }}>
                    {paidInstallmentsInfo.map((p) => (
                      <li key={p._id}>
                        Parcela {p.installmentNumber} — {formatCurrency(p.amount)} em {formatDate(p.date)}
                        {p.paymentStatus === 'pago' ? ' ✓ Pago' : ' (pendente)'}
                      </li>
                    ))}
                  </ul>
                  <p style={{ margin: '6px 0 0 0' }}>
                    Edite abaixo <strong>apenas esta parcela</strong>. As demais não serão alteradas.
                  </p>
                </div>
              )}

              {/* Seção 1: Informações Básicas */}
              <div style={{
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '15px', 
                  color: '#374151', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <MdDescription /> Informações Básicas
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>
                      Tipo da Transação *
                    </span>
                    <select
                      className="mac-input"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value, category: '' })}
                      required
                      style={{ background: '#fff' }}
                    >
                      <option value="receita">Receita</option>
                      <option value="despesa">Despesa</option>
                    </select>
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>
                      Categoria *
                    </span>
                    <select
                      className="mac-input"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      style={{ background: '#fff' }}
                    >
                      <option value="">Selecione...</option>
                      {categories[formData.type].map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', gridColumn: '1 / -1' }}>
                    <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>
                      Descrição
                    </span>
                    <input
                      type="text"
                      className="mac-input"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Ex: Consulta com Dr. João, Compra de materiais..."
                      style={{ background: '#fff' }}
                    />
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative', gridColumn: '1 / -1' }}>
                    <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>
                      {formData.type === 'receita' ? 'Paciente' : 'Fornecedor'}
                    </span>
                    {formData.type === 'receita' ? (
                      <>
                        <input
                          type="text"
                          className="mac-input"
                          placeholder="Digite para buscar..."
                          value={patientSearch}
                          onChange={(e) => {
                            setPatientSearch(e.target.value);
                            setShowPatientDropdown(true);
                          }}
                          onFocus={() => setShowPatientDropdown(true)}
                          style={{ background: '#fff' }}
                        />
                        {showPatientDropdown && patientSearch && (
                          <div 
                            data-patient-dropdown
                            style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            marginTop: '4px'
                          }}>
                            {patients
                              .filter(p => p.name.toLowerCase().includes(patientSearch.toLowerCase()))
                              .slice(0, 5)
                              .map(patient => (
                                <div
                                  key={patient._id || patient.id}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setFormData({ ...formData, patientId: patient._id || patient.id, supplier: '' });
                                    setPatientSearch(patient.name);
                                    setShowPatientDropdown(false);
                                  }}
                                  style={{
                                    padding: '12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                                >
                                  <div style={{ fontWeight: 500, fontSize: '14px' }}>{patient.name}</div>
                                  {patient.cpf && (
                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                                      CPF: {patient.cpf}
                                    </div>
                                  )}
                                </div>
                              ))}
                            {patients.filter(p => p.name.toLowerCase().includes(patientSearch.toLowerCase())).length === 0 && (
                              <div style={{ padding: '12px', color: '#9ca3af', fontSize: '14px' }}>
                                Nenhum paciente encontrado
                              </div>
                            )}
                            <div
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setFormData({ ...formData, patientId: '', supplier: '' });
                                setPatientSearch('');
                                setShowPatientDropdown(false);
                              }}
                              style={{
                                padding: '12px',
                                cursor: 'pointer',
                                color: '#ef4444',
                                fontSize: '14px',
                                fontWeight: 500,
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                            >
                              Limpar seleção
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <input
                        type="text"
                        className="mac-input"
                        placeholder="Nome do fornecedor"
                        value={formData.supplier}
                        onChange={(e) => setFormData({ ...formData, supplier: e.target.value, patientId: '' })}
                        style={{ background: '#fff' }}
                      />
                    )}
                  </label>
                </div>
              </div>

              {/* Seção 2: Valor e Data */}
              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #bae6fd'
              }}>
                <h3 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '15px', 
                  color: '#0c4a6e', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <MdMonetizationOn /> Valor e Vencimento
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.9rem', color: '#0c4a6e', fontWeight: 500 }}>
                      Valor Total (R$) *
                    </span>
                    <input
                      type="text"
                      className="mac-input"
                      value={formData.amount}
                      onChange={handleAmountChange}
                      placeholder="0,00"
                      required
                      style={{ 
                        background: '#fff',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: formData.type === 'receita' ? '#22c55e' : '#ef4444'
                      }}
                    />
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.9rem', color: '#0c4a6e', fontWeight: 500 }}>
                      Data *
                    </span>
                    <input
                      type="date"
                      className="mac-input"
                      value={formData.date}
                      onChange={(e) => {
                        setFormData({ ...formData, date: e.target.value });
                        unlockSchedule();
                      }}
                      required
                      style={{ background: '#fff' }}
                    />
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '0.9rem', color: '#0c4a6e', fontWeight: 500 }}>
                      Status *
                    </span>
                    <select
                      className="mac-input"
                      value={formData.paymentStatus}
                      onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
                      required
                      style={{ background: '#fff' }}
                    >
                      <option value="pendente">Pendente</option>
                      <option value="pago">Pago</option>
                      <option value="atrasado">Atrasado</option>
                    </select>
                  </label>
                </div>
              </div>

              {/* Seção 3: Forma de Pagamento */}
              <div style={{
                background: '#fff',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '15px', 
                  color: '#374151', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <MdPayment /> Forma de Pagamento
                </h3>

                {/* Pagamento À Vista */}
                {!formData.hasInstallments && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', alignItems: 'end' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500 }}>
                        Método de Pagamento *
                      </span>
                      <select
                        className="mac-input"
                        value={formData.paymentMethod}
                        onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                        required
                      >
                        <option value="dinheiro">Dinheiro</option>
                        <option value="pix">PIX</option>
                        <option value="cartao_debito">Cartão Débito</option>
                        <option value="cartao_credito">Cartão Crédito</option>
                        <option value="transferencia">Transferência</option>
                        <option value="boleto">Boleto</option>
                        <option value="outro">Outro</option>
                      </select>
                    </label>
                    
                    {/* Toggle de Parcelamento */}
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#6b7280',
                      padding: '10px 12px',
                      background: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    >
                      <input
                        type="checkbox"
                        checked={formData.hasInstallments}
                        onChange={(e) => {
                          setFormData({ 
                            ...formData, 
                            hasInstallments: e.target.checked,
                            installments: e.target.checked ? formData.installments : 1
                          });
                          unlockSchedule();
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Parcelar</span>
                    </label>
                  </div>
                )}
                
                {formData.hasInstallments && (
                  <div>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#374151',
                      padding: '10px 12px',
                      background: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      marginBottom: '20px',
                      fontWeight: 500
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.hasInstallments}
                        onChange={(e) => {
                          setFormData({ 
                            ...formData, 
                            hasInstallments: e.target.checked,
                            installments: e.target.checked ? formData.installments : 1
                          });
                          unlockSchedule();
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Parcelar este pagamento</span>
                    </label>

                    {/* Card único com Parcelas */}
                    <div style={{
                      background: '#fff',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb'
                    }}>
                      {/* Parcelas */}
                      <div>
                        <h4 style={{ 
                          margin: '0 0 12px 0', 
                          fontSize: '13px', 
                          color: '#374151', 
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <MdTimeline /> Parcelamento
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '0.85rem', color: '#6e6e73', fontWeight: 500 }}>
                              Número de Parcelas
                            </span>
                            <select
                              className="mac-input"
                              value={formData.installments}
                              onChange={(e) => {
                                setFormData({ ...formData, installments: parseInt(e.target.value) });
                                unlockSchedule();
                              }}
                              style={{ background: '#fff' }}
                            >
                              {[...Array(12)].map((_, i) => {
                                const totalAmount = parseAmountToNumber(formData.amount);
                                const installmentValue = totalAmount / (i + 1);
                                
                                return (
                                  <option key={i + 1} value={i + 1}>
                                    {i + 1}x de {formatCurrency(installmentValue)}
                                  </option>
                                );
                              })}
                            </select>
                          </label>

                          <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontSize: '0.85rem', color: '#6e6e73', fontWeight: 500 }}>
                              Forma de Pagamento
                            </span>
                            <select
                              className="mac-input"
                              value={formData.installmentPaymentMethod}
                              onChange={(e) => {
                                setFormData({ ...formData, installmentPaymentMethod: e.target.value });
                                unlockSchedule();
                              }}
                              style={{ background: '#fff' }}
                            >
                              <option value="cartao_credito">Cartão Crédito</option>
                              <option value="boleto">Boleto</option>
                              <option value="pix">PIX</option>
                              <option value="cartao_debito">Débito</option>
                              <option value="transferencia">Transferência</option>
                              <option value="outro">Outro</option>
                            </select>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Parcelas detalhadas */}
                    <div style={{
                      marginTop: '20px',
                      background: '#f9fafb',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      padding: '16px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                      }}>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                          Lançamentos Gerados
                        </h4>
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={recalculateInstallments}
                          style={{ fontSize: '12px' }}
                        >
                          Recalcular parcelas
                        </button>
                      </div>
                      {installmentSchedule.length === 0 ? (
                        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                          Informe o valor total, número de parcelas e data inicial para gerar os lançamentos.
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {installmentSchedule.map((inst, index) => {
                            const isPaid = inst.paymentStatus === 'pago';
                            return (
                            <div
                              key={`${inst.installmentNumber || index}-${index}`}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                                gap: '12px',
                                background: isPaid ? '#f3f4f6' : '#fff',
                                padding: '12px',
                                borderRadius: '10px',
                                border: '1px solid #e5e7eb',
                                opacity: isPaid ? 0.75 : 1,
                                alignItems: 'end'
                              }}
                            >
                              <div>
                                <span style={{ fontSize: '12px', color: '#9ca3af' }}>Parcela</span>
                                <div style={{ fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {inst.installmentNumber > 0
                                    ? `${inst.installmentNumber}/${scheduleCount}`
                                    : 'Entrada'}
                                  {isPaid && (
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '999px',
                                      background: '#dcfce7',
                                      color: '#15803d',
                                      fontSize: '11px',
                                      fontWeight: 600
                                    }}>
                                      Pago
                                    </span>
                                  )}
                                </div>
                              </div>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <span style={{ fontSize: '12px', color: '#6e6e73' }}>Data</span>
                                <input
                                  type="date"
                                  className="mac-input"
                                  value={inst.date}
                                  disabled={isPaid}
                                  onChange={(e) => handleInstallmentFieldChange(index, 'date', e.target.value)}
                                />
                              </label>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <span style={{ fontSize: '12px', color: '#6e6e73' }}>Valor</span>
                                <input
                                  type="text"
                                  className="mac-input"
                                  value={inst.amount}
                                  disabled={isPaid}
                                  onChange={(e) => handleInstallmentFieldChange(index, 'amount', formatCurrencyInput(e.target.value))}
                                  placeholder="0,00"
                                />
                              </label>
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <span style={{ fontSize: '12px', color: '#6e6e73' }}>Forma de Pagamento</span>
                                <select
                                  className="mac-input"
                                  value={inst.paymentMethod}
                                  disabled={isPaid}
                                  onChange={(e) => handleInstallmentFieldChange(index, 'paymentMethod', e.target.value)}
                                >
                                  <option value="dinheiro">Dinheiro</option>
                                  <option value="pix">PIX</option>
                                  <option value="cartao_debito">Cartão Débito</option>
                                  <option value="cartao_credito">Cartão Crédito</option>
                                  <option value="transferencia">Transferência</option>
                                  <option value="boleto">Boleto</option>
                                  <option value="outro">Outro</option>
                                </select>
                              </label>
                            </div>
                          );
                          })}
                        </div>
                      )}
                      <div style={{
                        marginTop: '12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: Math.abs(differenceAmount) < 0.01 ? '#22c55e' : '#ef4444'
                      }}>
                        Diferença atual: {formatCurrency(differenceAmount)}
                      </div>
                    </div>

                    {/* Resumo do Parcelamento */}
                    <div style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      color: '#fff',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      marginTop: '20px'
                    }}>
                      <h4 style={{
                        margin: '0 0 16px 0',
                        fontSize: '14px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <MdReceipt /> Resumo do Pagamento
                      </h4>
                      {(() => {
                        const totalAmount = totalAmountValue;
                        const totalParcelado = totalInstallmentAmount;
                        const methodSet = installmentSchedule.length
                          ? Array.from(new Set(installmentSchedule.map(inst => inst.paymentMethod)))
                          : [formData.installmentPaymentMethod];
                        const methodLabel = methodSet.length > 1
                          ? 'Múltiplos métodos'
                          : methodSet[0]?.replace('_', ' ') || 'cartão crédito';
                        const amountsEqual = installmentSchedule.length
                          ? installmentSchedule.every(inst => parseAmountToNumber(inst.amount) === parseAmountToNumber(installmentSchedule[0].amount))
                          : true;
                        const referenceValue = installmentSchedule.length
                          ? parseAmountToNumber(installmentSchedule[0].amount)
                          : (totalAmount / Math.max(1, formData.installments || 1));

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px' }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '8px 0',
                              borderBottom: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              <span style={{ opacity: 0.9 }}>Valor Total:</span>
                              <span style={{ fontWeight: 700, fontSize: '16px' }}>{formatCurrency(totalAmount)}</span>
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: 'rgba(255,255,255,0.15)',
                              borderRadius: '6px'
                            }}>
                              <span>{scheduleCount}x via {methodLabel}{amountsEqual ? '' : ' (valores personalizados)'}</span>
                              <span style={{ fontWeight: 700 }}>
                                {amountsEqual ? formatCurrency(referenceValue) : 'Valores personalizados'}
                              </span>
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: 'rgba(255,255,255,0.15)',
                              borderRadius: '6px'
                            }}>
                              <span>Total Parcelado:</span>
                              <span style={{ fontWeight: 700 }}>{formatCurrency(totalParcelado)}</span>
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: 'rgba(255,255,255,0.15)',
                              borderRadius: '6px',
                              color: Math.abs(differenceAmount) < 0.01 ? '#dcfce7' : '#fee2e2'
                            }}>
                              <span>Diferença:</span>
                              <span style={{ fontWeight: 700 }}>
                                {formatCurrency(differenceAmount)}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Seção 4: Observações */}
              <div>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontSize: '0.9rem', color: '#6e6e73', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MdNotes /> Observações
                  </span>
                  <textarea
                    className="mac-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Adicione informações extras sobre esta transação..."
                    rows="3"
                    style={{ resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </label>
              </div>

              {/* Botões de Ação */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '16px',
                borderTop: '2px solid #f3f4f6'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                  <input
                    type="checkbox"
                    id="keepFormOpen"
                    checked={keepFormOpen}
                    onChange={(e) => setKeepFormOpen(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <label htmlFor="keepFormOpen" style={{ cursor: 'pointer', margin: 0 }}>
                    Continuar lançando
                  </label>
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" className="ghost-button" onClick={resetForm}>
                    Cancelar
                  </button>
                  <button type="submit" className="mac-button">
                    {editingId ? 'Atualizar' : 'Salvar'}
                  </button>
                </div>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Filtros e Busca */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative', background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 10, display: 'flex', alignItems: 'center', padding: '0 12px', height: 40, boxShadow: '0 1px 2px rgba(28,28,30,0.04)', minWidth: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Buscar por descrição, categoria, valor..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', padding: '0 8px', fontSize: 13, color: '#1C1C1E', background: 'transparent', fontFamily: 'inherit', letterSpacing: '-0.005em', minWidth: 0 }}
          />
        </div>

        <select
          className="mac-input"
          value={filterMode}
          onChange={(e) => {
            const newMode = e.target.value;
            setFilterMode(newMode);
            if (newMode === 'period') {
              const today = new Date();
              const first = new Date(today.getFullYear(), today.getMonth(), 1);
              const last = new Date(today.getFullYear(), today.getMonth() + 2, 0);
              const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              setStartDate(fmt(first));
              setEndDate(fmt(last));
            }
          }}
          style={{ width: 118, flexShrink: 0 }}
        >
          <option value="month">Por Mês</option>
          <option value="year">Por Ano</option>
          <option value="period">Período</option>
        </select>

        {filterMode === 'month' && (
          <>
            <select className="mac-input" value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} style={{ width: 118, flexShrink: 0 }}>
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleDateString('pt-BR', { month: 'long' })}</option>
              ))}
            </select>
            <select className="mac-input" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} style={{ width: 84, flexShrink: 0 }}>
              {[...Array(5)].map((_, i) => { const year = new Date().getFullYear() - 2 + i; return <option key={year} value={year}>{year}</option>; })}
            </select>
          </>
        )}

        {filterMode === 'year' && (
          <select className="mac-input" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} style={{ width: 84, flexShrink: 0 }}>
            {[...Array(5)].map((_, i) => { const year = new Date().getFullYear() - 2 + i; return <option key={year} value={year}>{year}</option>; })}
          </select>
        )}

        {filterMode === 'period' && (
          <>
            <input type="date" className="mac-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ width: 138, flexShrink: 0 }} />
            <input type="date" className="mac-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ width: 138, flexShrink: 0 }} />
          </>
        )}

        <select className="mac-input" value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ width: 134, flexShrink: 0 }}>
          <option value="">Todos os tipos</option>
          <option value="receita">Receitas</option>
          <option value="despesa">Despesas</option>
        </select>

        <select className="mac-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ width: 140, flexShrink: 0 }}>
          <option value="">Todos os status</option>
          <option value="pago">Pago</option>
          <option value="pendente">Pendente</option>
          <option value="atrasado">Atrasado</option>
        </select>
      </div>

      {/* Tabela de Transações */}
      <div style={{ background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22, overflow: 'hidden', boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1.8fr 1.1fr 1fr 110px 90px', gap: 16, padding: '14px 22px', background: '#FAFAFB', borderBottom: '1px solid #E5E5EA', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6E6E73', alignItems: 'center' }}>
          {[
            { key: 'date', label: 'Vencimento' },
            { key: 'patientId', label: 'Paciente / Fornecedor' },
            { key: 'category', label: 'Categoria' },
          ].map(({ key, label }) => (
            <div key={key} onClick={() => handleSort(key)} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              {label}
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                <path d={sortConfig.key === key && sortConfig.direction === 'asc' ? 'M5 8.5v-7M5 1.5l2.5 2.5M5 1.5L2.5 4' : 'M5 1.5v7M5 8.5l2.5-2.5M5 8.5L2.5 6'} stroke={sortConfig.key === key ? '#1C1C1E' : '#A8A8AE'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          ))}
          <div onClick={() => handleSort('amount')} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', justifyContent: 'flex-end' }}>
            Valor
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
              <path d={sortConfig.key === 'amount' && sortConfig.direction === 'asc' ? 'M5 8.5v-7M5 1.5l2.5 2.5M5 1.5L2.5 4' : 'M5 1.5v7M5 8.5l2.5-2.5M5 8.5L2.5 6'} stroke={sortConfig.key === 'amount' ? '#1C1C1E' : '#A8A8AE'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div onClick={() => handleSort('paymentStatus')} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            Status
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
              <path d="M5 1.5v7M5 8.5l2.5-2.5M5 8.5L2.5 6" stroke="#A8A8AE" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ textAlign: 'right' }}>Ações</div>
        </div>

        {/* Rows */}
        {sortedTransactions.length === 0 ? (
          <div style={{ padding: '48px 22px', textAlign: 'center', color: '#8E8E93', fontSize: 14 }}>
            Nenhuma transação encontrada para o período selecionado
          </div>
        ) : (
          sortedTransactions.map((transaction, index) => {
            const effectiveStatus = getEffectiveStatus(transaction);
            const statusChips = {
              pago:       { bg: '#E6F1EB', fg: '#3D8068', dot: '#3D8068', label: 'Pago' },
              pendente:   { bg: '#FBEEDF', fg: '#A66730', dot: '#D4923A', label: 'Pendente' },
              atrasado:   { bg: '#FBE0E5', fg: '#9F4A60', dot: '#9F4A60', label: 'Atrasado' },
              vence_hoje: { bg: '#EFE3F0', fg: '#7A4A86', dot: '#7A4A86', label: 'Vence Hoje' },
            };
            const stat = statusChips[effectiveStatus] || statusChips.pendente;

            let displayName = '';
            if (transaction.patientId) {
              const patient = patients.find(p => (p._id || p.id) === transaction.patientId);
              displayName = patient?.name || 'Paciente não encontrado';
            } else if (transaction.supplier) {
              displayName = transaction.supplier;
            }
            const av = displayName ? avatarTone(displayName) : { bg: '#F2F2F7', fg: '#8E8E93' };

            const methodLabel = transaction.paymentMethod === 'cartao_credito' ? 'Cartão Crédito' :
              transaction.paymentMethod === 'cartao_debito' ? 'Cartão Débito' :
              transaction.paymentMethod.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
            const installLabel = transaction.installmentNumber > 0
              ? ` · ${transaction.installmentNumber}/${transaction.totalInstallments || transactions.filter(t => t.installmentGroup === transaction.installmentGroup && t.installmentNumber > 0).length || 1}`
              : transaction.installmentNumber === 0 ? ' · Entrada' : '';

            return (
              <div key={transaction._id} style={{ display: 'grid', gridTemplateColumns: '120px 1.8fr 1.1fr 1fr 110px 90px', gap: 16, padding: '16px 22px', borderTop: index === 0 ? 'none' : '1px solid #F2F2F7', alignItems: 'center' }}>
                {/* Vencimento */}
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatDate(transaction.date)}
                </div>

                {/* Paciente/Fornecedor */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  {displayName ? (
                    <div style={{ width: 34, height: 34, borderRadius: 999, background: av.bg, color: av.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, letterSpacing: '-0.01em', flexShrink: 0 }}>
                      {getInitials(displayName)}
                    </div>
                  ) : (
                    <div style={{ width: 34, height: 34, borderRadius: 999, background: '#F2F2F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    </div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: displayName ? '#1C1C1E' : '#8E8E93', letterSpacing: '-0.008em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: displayName ? 'normal' : 'italic' }}>
                      {displayName || 'Não informado'}
                    </div>
                    {transaction.description && (
                      <div style={{ fontSize: 11.5, color: '#8E8E93', marginTop: 2, letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {transaction.description.replace(/ - Parcela \d+\/\d+$/, '').replace(/ - Entrada$/, '')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Categoria */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 500, color: '#1C1C1E', letterSpacing: '-0.005em' }}>{transaction.category}</div>
                  <div style={{ fontSize: 11.5, color: '#8E8E93', marginTop: 2, letterSpacing: '-0.005em' }}>{methodLabel}{installLabel}</div>
                </div>

                {/* Valor */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.025em', color: transaction.type === 'receita' ? '#3D8068' : '#9F4A60', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatCurrency(transaction.amount)}
                  </div>
                </div>

                {/* Status */}
                <div style={{ position: 'relative' }} data-status-dropdown>
                  <div
                    onClick={() => setEditingStatusId(editingStatusId === transaction._id ? null : transaction._id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, letterSpacing: '-0.005em', color: stat.fg, background: stat.bg, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', cursor: 'pointer' }}
                  >
                    <span style={{ width: 5, height: 5, borderRadius: 999, background: stat.dot }} />
                    {stat.label}
                  </div>
                  {editingStatusId === transaction._id && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: '#fff', border: '1px solid #E5E5EA', borderRadius: 10, boxShadow: '0 4px 16px -4px rgba(28,28,30,0.15)', zIndex: 1000, minWidth: 120, overflow: 'hidden' }}>
                      {[
                        { key: 'pago',     label: 'Pago',     color: '#3D8068' },
                        { key: 'pendente', label: 'Pendente', color: '#A66730' },
                        { key: 'atrasado', label: 'Atrasado', color: '#9F4A60' },
                      ].map((s, si) => (
                        <div key={s.key} onMouseDown={() => handleStatusChange(transaction._id, s.key)} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: s.color, fontWeight: 600, borderBottom: si < 2 ? '1px solid #F2F2F7' : 'none' }} onMouseEnter={(e) => e.currentTarget.style.background = '#F9F9F9'} onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
                          {s.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="ghost-button" onClick={() => handleViewTransaction(transaction)} style={{ fontSize: 12 }}>
                    Ver
                  </button>
                </div>
              </div>
            );
          })
        )}

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #F2F2F7', background: '#FAFAFB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#6E6E73', letterSpacing: '-0.005em' }}>
          <span>{sortedTransactions.length} transaç{sortedTransactions.length === 1 ? 'ão' : 'ões'}</span>
          <div style={{ display: 'flex', gap: 28 }}>
            <div>
              <span style={{ color: '#8E8E93', marginRight: 6 }}>Receitas:</span>
              <span style={{ fontWeight: 700, color: '#3D8068', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(sortedTransactions.filter(t => t.type === 'receita').reduce((sum, t) => sum + t.amount, 0))}
              </span>
            </div>
            <div>
              <span style={{ color: '#8E8E93', marginRight: 6 }}>Despesas:</span>
              <span style={{ fontWeight: 700, color: '#9F4A60', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(sortedTransactions.filter(t => t.type === 'despesa').reduce((sum, t) => sum + t.amount, 0))}
              </span>
            </div>
            <div>
              <span style={{ color: '#8E8E93', marginRight: 6 }}>Saldo:</span>
              <span style={{ fontWeight: 700, color: '#1C1C1E', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(sortedTransactions.filter(t => t.type === 'receita').reduce((sum, t) => sum + t.amount, 0) - sortedTransactions.filter(t => t.type === 'despesa').reduce((sum, t) => sum + t.amount, 0))}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Visualização */}
      {viewingTransaction && (
        <div className="modal-overlay" onClick={() => setViewingTransaction(null)}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              maxWidth: '600px',
              maxHeight: '90vh',
              padding: 0,
              overflow: 'hidden'
            }}
          >
            <div style={{
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '32px'
            }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '2px solid #f3f4f6'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>
                Detalhes da Transação
              </h2>
              <button 
                onClick={() => setViewingTransaction(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer',
                  color: '#9ca3af'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Tipo</label>
                <div style={{ fontSize: '15px', fontWeight: 500 }}>
                  {viewingTransaction.type === 'receita' ? 'Receita' : 'Despesa'}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Categoria</label>
                <div style={{ fontSize: '15px', fontWeight: 500 }}>{viewingTransaction.category}</div>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Descrição</label>
                <div style={{ fontSize: '15px', fontWeight: 500 }}>
                  {viewingTransaction.description.replace(/ - Parcela \d+\/\d+$/, '').replace(/ - Entrada$/, '')}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Valor</label>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 600,
                  color: viewingTransaction.type === 'receita' ? '#22c55e' : '#ef4444'
                }}>
                  {formatCurrency(viewingTransaction.amount)}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Vencimento</label>
                  <div style={{ fontSize: '15px', fontWeight: 500 }}>{formatDate(viewingTransaction.date)}</div>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Status</label>
                  <div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: `${getStatusColor(getEffectiveStatus(viewingTransaction))}15`,
                      color: getStatusColor(getEffectiveStatus(viewingTransaction))
                    }}>
                      {getEffectiveStatus(viewingTransaction) === 'vence_hoje' ? 'Vence Hoje' : 
                       getEffectiveStatus(viewingTransaction).charAt(0).toUpperCase() + getEffectiveStatus(viewingTransaction).slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Forma de Pagamento</label>
                <div style={{ fontSize: '15px', fontWeight: 500 }}>
                  {viewingTransaction.paymentMethod === 'cartao_credito' ? 'Cartão Crédito' : 
                   viewingTransaction.paymentMethod === 'cartao_debito' ? 'Cartão Débito' :
                   viewingTransaction.paymentMethod.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {viewingTransaction.installmentNumber !== null && viewingTransaction.installmentNumber !== undefined && viewingTransaction.installmentNumber > 0 && (
                    <span style={{ color: '#6b7280', fontWeight: 600, marginLeft: '8px' }}>
                      {viewingTransaction.installmentNumber}/{viewingTransaction.totalInstallments || (() => {
                        const groupTransactions = transactions.filter(t => 
                          t.installmentGroup === viewingTransaction.installmentGroup && t.installmentNumber > 0
                        );
                        return groupTransactions.length || 1;
                      })()}
                    </span>
                  )}
                  {viewingTransaction.installmentNumber === 0 && (
                    <span style={{ color: '#6b7280', fontWeight: 600, marginLeft: '8px' }}>Entrada</span>
                  )}
                </div>
              </div>

              {viewingTransaction.patientId && (
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Paciente</label>
                  <div style={{ fontSize: '15px', fontWeight: 500 }}>
                    {(() => {
                      const patient = patients.find(p => (p._id || p.id) === viewingTransaction.patientId);
                      return patient ? patient.name : 'Não especificado';
                    })()}
                  </div>
                </div>
              )}

              {viewingTransaction.notes && (
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Observações</label>
                  <div style={{ fontSize: '15px', fontWeight: 500 }}>{viewingTransaction.notes}</div>
                </div>
              )}

              {/* Seção de Parcelas */}
              {viewingInstallments.length > 0 && (
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  background: '#f9fafb',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                    Detalhamento do Parcelamento
                  </h4>
                  
                  {/* Valor Total */}
                  <div style={{ marginBottom: '12px', padding: '12px', background: '#fff', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Valor Total</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                      {formatCurrency(viewingInstallments.reduce((sum, t) => sum + t.amount, 0))}
                    </div>
                  </div>

                  {/* Lista de Parcelas */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {viewingInstallments.sort((a, b) => a.installmentNumber - b.installmentNumber).map((inst) => (
                      <div
                        key={inst._id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 12px',
                          background: inst._id === viewingTransaction._id ? '#eff6ff' : '#fff',
                          borderRadius: '8px',
                          border: inst._id === viewingTransaction._id ? '2px solid #3b82f6' : '1px solid #e5e7eb'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                            {inst.installmentNumber === 0 ? 'Entrada' : `Parcela ${inst.installmentNumber}/${viewingInstallments.filter(i => i.installmentNumber > 0).length}`}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            {formatDate(inst.date)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', marginRight: '12px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                            {formatCurrency(inst.amount)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            {inst.paymentMethod === 'cartao_credito' ? 'Cartão Créd.' : 
                             inst.paymentMethod === 'cartao_debito' ? 'Cartão Déb.' :
                             inst.paymentMethod.charAt(0).toUpperCase() + inst.paymentMethod.slice(1)}
                          </div>
                        </div>
                        <div>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: `${getStatusColor(getEffectiveStatus(inst))}15`,
                            color: getStatusColor(getEffectiveStatus(inst))
                          }}>
                            {getEffectiveStatus(inst) === 'vence_hoje' ? 'Vence Hoje' : 
                             getEffectiveStatus(inst) === 'pago' ? 'Pago' :
                             getEffectiveStatus(inst) === 'pendente' ? 'Pendente' :
                             getEffectiveStatus(inst) === 'atrasado' ? 'Atrasado' : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              paddingTop: '24px',
              marginTop: '24px',
              borderTop: '2px solid #f3f4f6'
            }}>
              <button
                className="ghost-button"
                onClick={() => setViewingTransaction(null)}
              >
                Fechar
              </button>
              <button
                className="mac-button"
                onClick={() => {
                  handleEdit(viewingTransaction);
                  setViewingTransaction(null);
                }}
              >
                Editar
              </button>
              <button
                className="ghost-button danger"
                onClick={() => {
                  setViewingTransaction(null);
                  handleDelete(viewingTransaction._id, viewingTransaction.installmentGroup);
                }}
              >
                Excluir
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {toast.show && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}
